/**
 * Shared persisted-state helpers for the Trace Studio panel tabs.
 *
 * The panel is unmounted whenever the user switches to a different Babylon
 * Inspector side-pane. To keep fetched data and UI state across that remount,
 * tabs mirror selected `useState` values into a module-level slot map (in
 * catalog-auth.ts). Each tab pairs a `useState` whose initial value is
 * `cachedSlot(key, fallback)` with a `useMirrorSlot(key, value)` effect that
 * writes back on change. Keep these patterns inline in the tabs (rather than
 * wrapped in a single hook) so React's exhaustive-deps lint correctly sees
 * the native `useState` setter as stable.
 */

import { useState, useEffect, useRef } from 'react';
import { Teams } from 'trace-viewer/cms';
import type { UserTeamResponseDto } from 'trace-viewer/cms';
import {
    getCatalogStateSlot, setCatalogStateSlot,
    getCatalogSelectedTeamId, setCatalogSelectedTeamId, onCatalogSelectedTeamIdChange,
    getCatalogToken, onCatalogTokenChange,
} from '../catalog-auth';
import { apiConfig } from '../catalog-api';

export function cachedSlot<T>(key: string, fallback: T): T {
    const cached = getCatalogStateSlot<T>(key);
    return cached !== undefined ? cached : fallback;
}

export function useMirrorSlot<T>(key: string, value: T): void {
    useEffect(() => { setCatalogStateSlot(key, value); }, [key, value]);
}

/**
 * Reads/writes the shared `selectedTeamId`. Re-renders the component on
 * external changes (team switches from another tab's perspective happen via
 * the panel header, so this guarantees every tab observes them).
 */
export function useSharedTeam(): readonly [string | null, (teamId: string | null) => void] {
    const [teamId, setTeamId] = useState<string | null>(getCatalogSelectedTeamId);
    useEffect(() => onCatalogSelectedTeamIdChange(setTeamId), []);
    return [teamId, setCatalogSelectedTeamId] as const;
}

/** Dedupes concurrent first-fetches; cleared once the request settles. */
let teamsFetch: Promise<UserTeamResponseDto[]> | null = null;

/**
 * The teams this user belongs to, through the same `shared.teams` slot the
 * studio panel fills — whichever panel opens first pays for the fetch, and
 * the other reads it back on mount.
 *
 * Also settles the selection: with no team chosen there is nothing to scope
 * content to, and AI usage has nothing to bill (the gateway answers
 * `no_billing_team`), so the first team stands in until the user picks.
 */
export function useTeams(): UserTeamResponseDto[] {
    const [teams, setTeams] = useState<UserTeamResponseDto[]>(() => cachedSlot('shared.teams', []));
    const [authTick, setAuthTick] = useState(0);
    useEffect(() => onCatalogTokenChange(() => setAuthTick(n => n + 1)), []);

    useEffect(() => {
        const token = getCatalogToken();
        if (!token) {
            // Signed out: another account's teams must not linger.
            if (teams.length > 0) {
                setCatalogStateSlot('shared.teams', []);
                setTeams([]);
            }
            return;
        }
        if (teams.length > 0) return;
        teamsFetch ??= (async () => {
            try {
                const res = await new Teams(apiConfig(token)).teamsList();
                return res.data ?? [];
            } catch (err) {
                console.error('[teams] fetch failed:', err);
                return [];
            } finally {
                teamsFetch = null;
            }
        })();
        let live = true;
        void teamsFetch.then(list => {
            if (!live || list.length === 0) return;
            setCatalogStateSlot('shared.teams', list);
            setTeams(list);
            if (!getCatalogSelectedTeamId()) setCatalogSelectedTeamId(list[0].teamId ?? null);
        });
        return () => { live = false; };
    }, [teams.length, authTick]);

    return teams;
}

/**
 * Like `cachedSlot` but namespaced by team id. Switching teams naturally
 * yields an empty cache (cold fetch); switching back restores it. Pass an
 * empty/null `teamId` to disable caching for that render.
 */
export function teamScopedSlot<T>(key: string, teamId: string | null | undefined, fallback: T): T {
    if (!teamId) return fallback;
    return cachedSlot(`${key}.${teamId}`, fallback);
}

export function useMirrorTeamScopedSlot<T>(key: string, teamId: string | null | undefined, value: T): void {
    const lastTeamRef = useRef(teamId);
    useEffect(() => {
        if (teamId !== lastTeamRef.current) {
            // The team just changed but `value` still holds the previous
            // team's state (these mirror effects run before the re-hydration
            // effect). Writing now would leak one team's data into another's
            // cache slot — skip; the re-hydrated value mirrors on the next run.
            lastTeamRef.current = teamId;
            return;
        }
        if (!teamId) return;
        setCatalogStateSlot(`${key}.${teamId}`, value);
    }, [key, teamId, value]);
}

// ============================================================================
// Refresh registry
// ============================================================================

/**
 * The currently-active tab registers its refresh handler here so the toolbar
 * refresh button can call it directly (and await the returned promise to
 * drive a spinner). At most one handler is active at a time.
 */
let _activeRefreshHandler: (() => void | Promise<void>) | null = null;

/** Helper used by each tab to register its refresh callback while it is the active tab. */
export function useCatalogRefresh(active: boolean, onRefresh: () => void | Promise<void>): void {
    useEffect(() => {
        if (!active) return;
        _activeRefreshHandler = onRefresh;
        return () => {
            if (_activeRefreshHandler === onRefresh) _activeRefreshHandler = null;
        };
    }, [active, onRefresh]);
}

/** The active tab's refresh handler, if any (consumed by the panel toolbar). */
export function getCatalogRefreshHandler(): (() => void | Promise<void>) | null {
    return _activeRefreshHandler;
}
